import { createContext, type ReactNode, useId } from "react";
import { MAX_METHOD, METHOD_PICKS } from "../../src/booking.ts";
import type { Line } from "./bookingDraft.ts";
import { Chip, Icon, SegmentedControl } from "./souvenir.ts";

// The fields the web's forms share (the booking form, Add a payment): Souvenir's labelled field, the phone's own
// picker dressed as one, the currencies, and a payment's line (who, how much in what, and how).

// My own currency in this trip (src/currency.ts: my member profile's here, else my account's, the profile's Your
// currency), for a form inside a trip to start from where the trip says none: TripPage provides it.
export const YourCurrency = createContext<string | null>(null);

// Currencies: those the trip already uses first (and mine), then every other; with none known, the US dollar and the
// euro lead rather than the alphabet's first (the UAE dirham).
export function currencyList(first: string[]): string[] {
  let all: string[];
  try {
    all = (Intl as unknown as { supportedValuesOf: (k: string) => string[] }).supportedValuesOf("currency");
  } catch {
    all = [];
  }
  const top = [...new Set(first.length > 0 ? first : ["USD", "EUR"])];
  return [...top, ...all.filter((c) => !top.includes(c))];
}

// One payment as typed (DESIGN.md, Records, `paid`): paid now or charged later (`charges`, with the day the card on
// file is charged), who paid or whose card (`who`: me first, then the others), the amount and its currency, and how
// it was paid, in a few words or one of the picks (which card, for one charged later). `n`: its number on the form,
// for what's said to a screen reader. `problem`: what's wrong with a field, by its name ("<line key>.amount").
export function PaymentFields({
  line,
  n,
  who,
  currencies,
  problem,
  onChange,
}: {
  line: Line;
  n: number;
  who: { value: string; label: string }[];
  currencies: string[];
  problem: (field: string) => string | null;
  onChange: (patch: Partial<Line>) => void;
}) {
  return (
    <>
      {/* Paid now, or charged later to a card on file, on a day (features/bookings-and-costs.md, "Charged later"). */}
      <SegmentedControl
        label={`When payment ${n} is paid`}
        value={line.later ? "later" : "now"}
        onChange={(v) => onChange({ later: v === "later" })}
        options={[
          { value: "now", label: "Paid now" },
          { value: "later", label: "Charged later" },
        ]}
      />
      {line.later && (
        <Field
          label="Charged on"
          field={`${line.key}.on`}
          problem={problem(`${line.key}.on`)}
          hint="The day the card on file is charged, as the booking says."
        >
          {(id) => (
            <input
              id={id}
              data-field={`${line.key}.on`}
              className={`sv-field-input${line.on ? "" : " is-empty"}`}
              type="date"
              value={line.on}
              aria-invalid={problem(`${line.key}.on`) ? true : undefined}
              onChange={(e) => onChange({ on: e.target.value })}
            />
          )}
        </Field>
      )}
      <Field label={line.later ? "Whose card" : "Paid by"} field={`${line.key}.by`} problem={problem(`${line.key}.by`)}>
        {(id) => (
          <Select
            id={id}
            field={`${line.key}.by`}
            value={line.by}
            onChange={(by) => onChange({ by })}
            options={[...(line.by ? [] : [{ value: "", label: line.later ? "Whose card?" : "Who paid?" }]), ...who]}
          />
        )}
      </Field>
      <div className="tm-booking-pair is-money">
        <Field label="Amount" field={`${line.key}.amount`} problem={problem(`${line.key}.amount`)}>
          {(id) => (
            <input
              id={id}
              data-field={`${line.key}.amount`}
              className="sv-field-input"
              inputMode="decimal"
              autoComplete="off"
              value={line.amount}
              aria-invalid={problem(`${line.key}.amount`) ? true : undefined}
              onChange={(e) => onChange({ amount: e.target.value })}
            />
          )}
        </Field>
        <Field label="Currency" field={`${line.key}.currency`} problem={null}>
          {(id) => (
            <Select
              id={id}
              field={`${line.key}.currency`}
              aria-label={`Currency of payment ${n}`}
              value={line.currency}
              onChange={(currency) => onChange({ currency })}
              options={withValue(
                currencies.map((c) => ({ value: c, label: c })),
                line.currency,
              )}
            />
          )}
        </Field>
      </div>
      <Field
        label={line.later ? "Which card" : "How"}
        field={`${line.key}.method`}
        problem={problem(`${line.key}.method`)}
        hint="Which card, by name."
      >
        {(id) => (
          <input
            id={id}
            data-field={`${line.key}.method`}
            className="sv-field-input"
            autoComplete="off"
            maxLength={MAX_METHOD}
            placeholder="Visa"
            value={line.method}
            aria-invalid={problem(`${line.key}.method`) ? true : undefined}
            onChange={(e) => onChange({ method: e.target.value })}
          />
        )}
      </Field>
      {!line.later && (
        <fieldset className="tm-booking-picks">
          <legend className="tm-hidden">How payment {n} was paid</legend>
          {METHOD_PICKS.map((pick) => (
            <Chip key={pick} selected={line.method === pick} onClick={() => onChange({ method: pick })}>
              {pick}
            </Chip>
          ))}
        </fieldset>
      )}
    </>
  );
}

// The value a select has, in its list even when the list doesn't know it (a zone or currency from elsewhere).
export const withValue = (options: { value: string; label: string }[], value: string) =>
  !value || options.some((o) => o.value === value) ? options : [{ value, label: value }, ...options];

// A labelled field, as Souvenir's text field draws one (`sv-field`): its label over it, a hint or what's wrong
// under it. `theirs`: said over it when someone else's change meanwhile shows in it.
export function Field({
  label,
  field,
  problem,
  hint,
  theirs,
  children,
}: {
  label: string;
  field: string;
  problem: string | null;
  hint?: string;
  theirs?: ReactNode;
  children: (id: string) => ReactNode;
}) {
  const id = useId();
  return (
    <div className={`sv-field tm-booking-field${problem ? " is-wrong" : ""}`} data-for={field}>
      <label className="sv-field-label" htmlFor={id}>
        {label}
      </label>
      {theirs}
      {children(id)}
      {problem ? (
        <span className="sv-field-hint tm-error">{problem}</span>
      ) : (
        hint && <span className="sv-field-hint">{hint}</span>
      )}
    </div>
  );
}

// The phone's own picker, dressed as Souvenir's field, its chevron at the end.
export function Select({
  id,
  field,
  value,
  options,
  onChange,
  "aria-label": label,
}: {
  id: string;
  field: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
  "aria-label"?: string;
}) {
  return (
    <span className="tm-booking-select">
      <select
        id={id}
        data-field={field}
        className="sv-field-input"
        value={value}
        {...(label ? { "aria-label": label } : {})}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <span className="tm-booking-chevron" aria-hidden="true">
        <Icon name="chevron" size={16} />
      </span>
    </span>
  );
}
